import React from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@mui/material/Tooltip';
import { DAYS, DAY_KEYS } from './constants';
import { getCalendarItemColor } from './eventColors';
import {
  isSameDay,
  miniCalendarEventAppliesToDay,
  miniCalendarShowsDotForEvent,
  miniCalendarFullShadeEventsForDay,
  getMiniCalendarEventDisplayName,
  formatMiniCalendarEventDateRange
} from './calendarUtils';

const MAX_DOTS = 4;

function MiniCalendarDayTooltipContent({ events }) {
  return (
    <div className='flex max-w-[min(260px,calc(100vw-2rem))] flex-col gap-2 py-0.5'>
      {events.map(ev => (
        <div
          key={`${ev.type}-${ev.id ?? ev.calendarDate}-${ev.badgeTitle ?? ev.title ?? ''}`}
        >
          <div className='font-semibold leading-snug text-white'>
            {getMiniCalendarEventDisplayName(ev)}
          </div>
          <div className='mt-0.5 text-[11px] leading-snug text-slate-200'>
            {formatMiniCalendarEventDateRange(ev)}
          </div>
        </div>
      ))}
    </div>
  );
}

MiniCalendarDayTooltipContent.propTypes = {
  events: PropTypes.arrayOf(PropTypes.object).isRequired
};

function colorForEvent(ev, events) {
  return getCalendarItemColor(ev, ev.colorIdx ?? events.indexOf(ev));
}

/** Split gradient proportional to count (equal slices per overlapping shaded event). */
function getMiniCalendarDayShade(shadeEvents, events) {
  const n = shadeEvents.length;
  if (n === 0) return {};
  if (n === 1) {
    const color = colorForEvent(shadeEvents[0], events);
    return {
      backgroundColor: color.bg,
      // a single event colored from Zoho can be fully saturated, so the day
      // number follows its badge text color instead of the default slate
      color: color.isCustom ? color.text : undefined
    };
  }
  const parts = [];
  for (let i = 0; i < n; i++) {
    const c = colorForEvent(shadeEvents[i], events).bg;
    const startPct = (i / n) * 100;
    const endPct = ((i + 1) / n) * 100;
    parts.push(`${c} ${startPct}%`, `${c} ${endPct}%`);
  }
  return { background: `linear-gradient(90deg, ${parts.join(', ')})` };
}

export default function MiniCalendar({
  year,
  month,
  today,
  events,
  onDayClick,
  selectedDay
}) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = Array.from({ length: firstDay + daysInMonth }, (_, i) =>
    i < firstDay ? null : new Date(year, month, i - firstDay + 1)
  );
  while (cells.length % 7 !== 0) cells.push(null);

  const cellEntries = cells.map((date, idx) => {
    if (!date) {
      const row = Math.floor(idx / 7);
      const col = idx % 7;
      return { key: `empty-${year}-${month}-r${row}-c${col}`, date: null };
    }
    return {
      key: `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`,
      date
    };
  });

  return (
    <div className='min-w-0 max-w-full'>
      <div className='grid grid-cols-7 gap-px sm:gap-0 mb-1'>
        {DAYS.map((d, i) => (
          <div
            key={DAY_KEYS[i]}
            className='text-center text-[10px] sm:text-xs font-semibold text-slate-400 py-0.5 sm:py-1'
          >
            {d}
          </div>
        ))}
      </div>
      <div className='grid grid-cols-7 gap-px sm:gap-0'>
        {cellEntries.map(({ key: cellKey, date }) => {
          if (!date) {
            return <div key={cellKey} className='min-w-0' />;
          }

          const isToday = isSameDay(date, today);
          const isSelected = selectedDay && isSameDay(date, selectedDay);
          const dayEvents = events.filter(ev =>
            miniCalendarEventAppliesToDay(date, ev)
          );
          const dotEvents = events.filter(ev =>
            miniCalendarShowsDotForEvent(date, ev)
          );
          const shadeEvents = miniCalendarFullShadeEventsForDay(date, events);
          const hasShade = !isToday && shadeEvents.length > 0;
          const shadeStyle = hasShade
            ? getMiniCalendarDayShade(shadeEvents, events)
            : {};

          const dotsToShow = dotEvents.slice(0, MAX_DOTS);
          const extraCount = dotEvents.length - MAX_DOTS;

          const eventCount = dayEvents.length;
          const eventSuffix = eventCount === 1 ? '' : 's';
          const eventLabel =
            eventCount > 0 ? `, ${eventCount} event${eventSuffix}` : '';
          const ariaLabel = `${date.getDate()}${eventLabel}`;

          let circleHoverClass = '';
          if (!isToday) {
            circleHoverClass = hasShade
              ? 'hover:brightness-[0.93]'
              : 'hover:bg-white/60';
          }

          return (
            <Tooltip
              key={cellKey}
              disableHoverListener={dayEvents.length === 0}
              title={
                dayEvents.length > 0 ? (
                  <MiniCalendarDayTooltipContent events={dayEvents} />
                ) : (
                  ''
                )
              }
              placement='top'
              arrow
              enterDelay={350}
              enterNextDelay={200}
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'rgb(30,41,59)',
                    color: '#fff',
                    maxWidth: 280,
                    px: 1.5,
                    py: 1,
                    border: '1px solid rgb(51,65,85)',
                    '& .MuiTooltip-arrow': {
                      color: 'rgb(30,41,59)'
                    }
                  }
                }
              }}
            >
              <button
                type='button'
                className='relative flex flex-col gap-0.5 items-center justify-center py-0.5 sm:py-1 min-w-0 cursor-pointer rounded-md hover:bg-slate-50/80 border-0 bg-transparent'
                onClick={() => onDayClick(date)}
                aria-label={ariaLabel}
              >
                <div
                  className={[
                    'w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-[10px] sm:text-xs rounded-full shrink-0',
                    'transition-all font-medium select-none',
                    isToday ? 'bg-blue-500 text-white font-bold shadow-md' : '',
                    hasShade ? 'text-slate-800' : '',
                    isSelected && !isToday ? 'ring-2 ring-blue-400 ring-offset-1' : '',
                    circleHoverClass
                  ].join(' ')}
                  style={isToday ? undefined : shadeStyle}
                >
                  {date.getDate()}
                </div>
                {dotEvents.length > 0 && (
                  <div className='flex items-center justify-center gap-0.5 mt-0.5 flex-wrap max-w-full'>
                    {dotsToShow.map(ev => {
                      const dotKey = `${ev.type}-${ev.id ?? ev.calendarDate}-${
                        ev.badgeTitle ?? ev.title ?? ''
                      }`;
                      return (
                        <span
                          key={dotKey}
                          className='w-1.5 h-1.5 rounded-full shrink-0'
                          style={{
                            backgroundColor: colorForEvent(ev, events).dot
                          }}
                          aria-hidden
                        />
                      );
                    })}
                    {extraCount > 0 && (
                      <span className='text-[9px] font-medium text-slate-400'>
                        +{extraCount}
                      </span>
                    )}
                  </div>
                )}
              </button>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

MiniCalendar.propTypes = {
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  today: PropTypes.instanceOf(Date).isRequired,
  events: PropTypes.arrayOf(PropTypes.object).isRequired,
  onDayClick: PropTypes.func.isRequired,
  selectedDay: PropTypes.instanceOf(Date)
};
